import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import '../component.css';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {sendAddLayer, sendRemoveLayer} from "../../api";
import SquareButton from "../common/SquareButton.js";
import HoldButton from "../common/HoldButton.js";
import "./Layers.css";
import Tooltip from '@mui/material/Tooltip';

function SortableItem({id, value, layer, setLayer, thumbRef}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id});

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: value === layer ? '#aad' : undefined,
    };

    return (
        <div
            ref={setNodeRef}
            className="LayerListItem"
            style={style}
            onClick={() => setLayer(value)}
        >
            <canvas
                ref={r => thumbRef(value, r)}
                width={140}
                height={100}
                style={{background: '#fff', width: 70, height: 50}}
            />
            <div style={{display: 'flex', width: '100%'}}>
                {/* Drag handle */}
                <div
                    className="Handle"
                    style={{padding: '0 8px', pointerEvents: 'all', width: '100%', cursor: 'grab'}}
                    onClick={(e) => { e.stopPropagation(); setLayer(value); }}
                    {...attributes}
                    {...listeners}
                >
                    {value}
                </div>
            </div>
        </div>
    );
}

export default function Layers(props) {
    const {t} = useTranslation();
    const [items, setItems] = useState(props.layerList || []);

    useEffect(() => {
        setItems(props.layerList || []);
    }, [props.layerList]);

    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: { distance: 5 },
    }));

    const handleDragEnd = (event) => {
        const {active, over} = event;
        if (props.disabled) return;
        if (!over || active.id === over.id) return;
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        props.setLayer(items[oldIndex]);
        props.moveLayer(oldIndex, newIndex);
    };

    return (
        <div className="Container">
            <div className="ComponentTitle">{t('layers.title')}</div>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    <div className="LayerListContainer">
                        {items.map((value) => (
                            <SortableItem
                                key={value}
                                id={value}
                                value={value}
                                layer={props.layer}
                                setLayer={props.setLayer}
                                thumbRef={props.thumbRef}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
            <div style={{display: 'flex', textAlign: 'right', justifyContent: 'flex-end', ...(props.disabled ? {opacity: 0.4, pointerEvents: 'none'} : {})}}>
                <Tooltip title={t('layers.addLayer')} placement="left">
                    <div style={{width: 'auto'}}>
                        <SquareButton icon="add" onClick={() => sendAddLayer()}/>
                    </div>
                </Tooltip>
                <Tooltip title={props.layerList.length <= 1 ? t('layers.cantRemoveLast') : t('layers.holdToRemove')} placement="left">
                    <div style={{width: 'auto'}}>
                        <HoldButton onClick={() => sendRemoveLayer(props.layer)} disabled={props.layerList.length <= 1}/>
                    </div>
                </Tooltip>
            </div>
        </div>
    );
}